import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { getInstanceEffectiveAgent, type InstanceEffectiveAgent } from '@/services/aiStudioInstanceSetupService';

interface InstanceEffectiveAgentPanelProps {
  instanceId: string;
}

export const InstanceEffectiveAgentPanel: React.FC<InstanceEffectiveAgentPanelProps> = ({
  instanceId,
}) => {
  const { t } = useLanguage();
  const [effective, setEffective] = useState<InstanceEffectiveAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    getInstanceEffectiveAgent(instanceId)
      .then((data) => {
        if (!cancelled) setEffective(data);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.instances.effectiveAgentLoadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  if (loading) {
    return (
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.instances.effectiveAgentLoading')}
      </p>
    );
  }

  if (errorKey || !effective) {
    return (
      <p role="alert" className="text-base text-[var(--color-error)]">
        {t(errorKey ?? 'aiStudio.instances.effectiveAgentLoadFailed')}
      </p>
    );
  }

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.instances.effectiveAgentTitle')}
      </h2>
      <p className="mt-1 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.instances.effectiveAgentHint')}
      </p>

      <dl className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.instances.effectiveAgentName')}
          </dt>
          <dd className="text-base text-[var(--color-surface-text)]">{effective.agentName}</dd>
        </div>
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.instances.effectiveAgentCode')}
          </dt>
          <dd className="text-base text-[var(--color-surface-text)]">{effective.agentCode ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.instances.effectiveAgentVersion')}
          </dt>
          <dd className="text-base text-[var(--color-surface-text)]">
            v{effective.versionNumber}
          </dd>
        </div>
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.instances.effectiveAgentPack')}
          </dt>
          <dd className="text-base text-[var(--color-surface-text)]">
            {effective.industryPackName ?? t('aiStudio.common.none')}
          </dd>
        </div>
      </dl>

      {effective.purpose && (
        <p className="mt-3 text-base text-[var(--color-surface-text)]">{effective.purpose}</p>
      )}

      {effective.tools.length > 0 && (
        <div className="mt-3">
          <h3 className="text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.instances.effectiveAgentTools')}
          </h3>
          <ul className="mt-1 list-disc pl-5 text-base text-[var(--color-surface-text-secondary)]">
            {effective.tools.map((tool) => (
              <li key={tool.toolName}>
                <span className="text-[var(--color-surface-text)]">{tool.toolName}</span>
                {tool.description ? ` — ${tool.description}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
